import { Injectable, OnDestroy } from '@angular/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { SettingsService } from '../settings/settings.service'

export enum Theme {
  dark = 'dark',
  light = 'light',
  auto = 'auto',
}

const BACKGROUND_COLOR = '#fff8f6'
const SECONDARY_BACKGROUND_COLOR = '#ffffff'
const DARK_BACKGROUND_COLOR = '#011610'
const SECONDARY_DARK_BACKGROUND_COLOR = '#142d22'

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()

  isLightTheme: BehaviorSubject<boolean> = new BehaviorSubject(true)
  useSecondaryBackgroundColor: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  )
  theme: BehaviorSubject<Theme>

  private prefersDark = window.matchMedia('(prefers-color-scheme: dark)')

  constructor(private settingsService: SettingsService) {
    this.isLightTheme
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((_) => this.updateAppBars())
    this.useSecondaryBackgroundColor
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((_) => this.updateAppBars())
    this.isLightTheme.next(!this.prefersDark.matches)
    this.prefersDark.addEventListener('change', (_) => {
      this.updateTheme()
    })
    const initialTheme =
      (this.settingsService.getInterfaceMode() as Theme) ?? Theme.auto
    this.theme = new BehaviorSubject<Theme>(initialTheme)
    this.theme.pipe(takeUntil(this._destroySubject$)).subscribe((theme) => {
      this.settingsService.setInterfaceMode(theme)
      this.updateTheme()
    })
  }

  ngOnDestroy(): void {
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  updateTheme() {
    switch (this.theme.value) {
      case Theme.auto:
        this.toggleDarkTheme(this.prefersDark.matches)
        break
      case Theme.dark:
        this.toggleDarkTheme(true)
        break
      case Theme.light:
        this.toggleDarkTheme(false)
        break
      default:
        break
    }
  }

  private toggleDarkTheme(addDarkTheme: boolean) {
    // Toggle the "dark" class on the document body
    document.body.classList.toggle('dark', addDarkTheme)
    this.isLightTheme.next(!addDarkTheme)
  }

  async updateAppBars() {
    if (this.settingsService.isAndroid) {
      await this.updateStatusBar()
      await this.updateNavigationBar()
    }
  }

  private async updateStatusBar() {
    let color: string
    if (this.isLightTheme.value) {
      color = this.useSecondaryBackgroundColor.value
        ? SECONDARY_BACKGROUND_COLOR
        : BACKGROUND_COLOR
    } else {
      color = this.useSecondaryBackgroundColor.value
        ? SECONDARY_DARK_BACKGROUND_COLOR
        : DARK_BACKGROUND_COLOR
    }

    await StatusBar.setBackgroundColor({
      color,
    })
    await StatusBar.setStyle({
      style: this.isLightTheme.value ? Style.Light : Style.Dark,
    })
  }

  private async updateNavigationBar() {
    const color = this.isLightTheme.value
      ? SECONDARY_BACKGROUND_COLOR
      : DARK_BACKGROUND_COLOR

    await NavigationBar.setColor({
      color,
      darkButtons: !this.isLightTheme.value,
    })
    await NavigationBar.setTransparency({ isTransparent: false })
  }
}
